// Where the API and the WebSocket actually live.
//
// On the WEB this is the empty string: same-origin, so `/api/v1/...` is a
// relative URL, the Vite proxy handles dev and Apache handles production.
// Nothing about the browser build changes.
//
// On NATIVE it must be ABSOLUTE, and the reason is not obvious.
//
// The shell serves its bundled assets from a Capacitor scheme handler that owns
// a whole origin inside the WebView. Every request to that origin is answered
// from the app bundle and NEVER touches the network. So while the bundle's
// origin was `https://mysocialcal.com`, a relative `/api/v1/auth/login` resolved
// against the bundle, matched no file, fell through to the SPA's index.html, and
// the client parsed HTML as JSON — surfacing as "Malformed response from server"
// with no request ever leaving the device.
//
// That is why the bundle's origin (`app.mysocialcal.com`, see
// `capacitor.config.json`) and the API's origin (below) MUST DIFFER. Point them
// at the same name again and the shell silently stops reaching the API.
//
// Consequence: on native these are CROSS-ORIGIN requests, so the server has to
// allow this exact origin by name. See `server/src/http/cors.ts`.
import { isNative } from './platform.js';

/**
 * The origin the bundled app is served from inside the WebView.
 *
 * ⚠ On iOS the scheme is `capacitor:`, NOT `https:`. Capacitor discards any
 * local scheme WKWebView already handles natively (`CAPInstanceDescriptor
 * .normalize()`), so `server.iosScheme: "https"` in `capacitor.config.json` is
 * silently ignored and the default `capacitor` is used. Android has no such
 * restriction and does use `https:`.
 *
 * This is the exact string the server must allowlist, and getting it wrong
 * fails as a CORS refusal with a perfectly healthy-looking API. It is exported
 * for documentation; nothing reads it at runtime.
 */
export const APP_ORIGIN_IOS = 'capacitor://app.mysocialcal.com';
export const APP_ORIGIN_ANDROID = 'https://app.mysocialcal.com';

/** The origin the real API and WebSocket are served from. */
export const API_ORIGIN = 'https://mysocialcal.com';

/**
 * Origin prefix for API and WebSocket URLs: `''` on the web (same-origin),
 * the absolute API origin inside the shell.
 *
 * Called per request rather than captured at module load: `isNative()` reads a
 * global Capacitor injects into the page, and pinning it in a module constant
 * would bake in whatever the value happened to be at import time.
 */
export function apiOrigin(): string {
  return isNative() ? API_ORIGIN : '';
}
